import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LogOut,
    UserCircle2,
    Film,
    ShieldCheck,
    Menu,
    X,
    ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const GUEST_PRIMARY = [
    { to: "/catalog", label: "Marketplace", testid: "nav-catalog" },
    { to: "/auctions", label: "Auctions", testid: "nav-auctions" },
    { to: "/sell", label: "Sell Your Rights", testid: "nav-sell" },
    { to: "/how-it-works", label: "How It Works", testid: "nav-how" },
];
const PRIMARY_BY_ROLE = {
    creator: [
        { to: "/creator", label: "Upload Studio", testid: "nav-creator" },
        { to: "/my-films", label: "My Films", testid: "nav-my-films" },
        { to: "/analytics", label: "Analytics", testid: "nav-analytics" },
        { to: "/how-it-works", label: "How It Works", testid: "nav-how" },
    ],
    buyer: [
        { to: "/catalog", label: "Marketplace", testid: "nav-catalog" },
        { to: "/auctions", label: "Auctions", testid: "nav-auctions" },
        { to: "/watchlist", label: "Watchlist", testid: "nav-watchlist" },
        { to: "/my-deals", label: "My Deals", testid: "nav-my-deals" },
    ],
    admin: [
        { to: "/admin", label: "Admin", testid: "nav-admin" },
        { to: "/catalog", label: "Marketplace", testid: "nav-catalog" },
        { to: "/auctions", label: "Auctions", testid: "nav-auctions" },
    ],
};
const SECONDARY_LINKS = [
    { to: "/legal-framework", label: "Legal Framework", testid: "nav-legal" },
    { to: "/crowdfunding", label: "Crowdfunding", testid: "nav-crowdfunding" },
    { to: "/about", label: "About Us", testid: "nav-about" },
    { to: "/contact", label: "Contact", testid: "nav-contact" },
    { to: "/kyc", label: "Verify Identity", testid: "nav-kyc" },
    { to: "/inbox", label: "Inbox", testid: "nav-inbox" },
];

// Strategic moat hubs — visible to all roles (guests see public panels only)
const PLATFORM_LINKS_BASE = [
    { to: "/legal", label: "Legal Trust Stack", testid: "nav-hub-legal", public: true },
    { to: "/intel", label: "Market Intelligence", testid: "nav-hub-intel", public: true },
    { to: "/economy", label: "Creator Economics", testid: "nav-hub-economy", public: false },
    { to: "/ai-studio", label: "AI Studio", testid: "nav-hub-ai", public: false },
    { to: "/distribution", label: "Distribution & Network", testid: "nav-hub-dist", public: true },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [platformOpen, setPlatformOpen] = useState(false);

    const linkCls = ({ isActive }) =>
        `text-xs tracking-widest uppercase transition-colors ${
            isActive ? "text-platinum" : "text-silver-200 hover:text-white"
        }`;

    const primaryLinks = user ? (PRIMARY_BY_ROLE[user.role] || GUEST_PRIMARY) : GUEST_PRIMARY;
    const platformLinks = user ? PLATFORM_LINKS_BASE : PLATFORM_LINKS_BASE.filter((p) => p.public);

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="sticky top-0 z-50 glass-strong border-b border-white/5"
            data-testid="nav-bar"
        >
            <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-4 md:py-5 flex items-center justify-between gap-3">
                <Link
                    to="/"
                    className="flex items-center gap-3 shrink-0 group"
                    data-testid="nav-logo"
                    onClick={() => setOpen(false)}
                >
                    <img
                        src="/logo.png"
                        alt="Silver Screen Xchange"
                        className="h-14 md:h-16 lg:h-[72px] w-auto drop-shadow-[0_4px_24px_rgba(229,228,226,0.35)] transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                    <div className="font-display text-xl md:text-2xl lg:text-[26px] tracking-tight leading-none">
                        <span className="silver-text">Silver Screen</span>
                        <span className="text-platinum ml-1.5">Xchange</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {primaryLinks.map((l) => (
                        <NavLink key={l.to} to={l.to} className={linkCls} data-testid={l.testid}>
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="relative">
                        <button
                            onClick={() => setPlatformOpen((v) => !v)}
                            onBlur={() => setTimeout(() => setPlatformOpen(false), 150)}
                            className="flex items-center gap-1 text-xs tracking-widest uppercase text-silver-200 hover:text-white"
                            data-testid="nav-platform"
                        >
                            Platform <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        <AnimatePresence>
                            {platformOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    className="absolute right-0 top-full mt-2 min-w-[240px] glass-strong border border-white/10 rounded-sm py-2 z-50"
                                >
                                    {platformLinks.map((l) => (
                                        <NavLink
                                            key={l.to}
                                            to={l.to}
                                            className="block px-4 py-2 text-xs tracking-widest uppercase text-silver-200 hover:bg-white/5 hover:text-white"
                                            data-testid={l.testid}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            {l.label}
                                        </NavLink>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setMoreOpen((v) => !v)}
                            onBlur={() => setTimeout(() => setMoreOpen(false), 150)}
                            className="flex items-center gap-1 text-xs tracking-widest uppercase text-silver-200 hover:text-white"
                            data-testid="nav-more"
                        >
                            More <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        <AnimatePresence>
                            {moreOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    className="absolute right-0 top-full mt-2 min-w-[200px] glass-strong border border-white/10 rounded-sm py-2 z-50"
                                >
                                    {SECONDARY_LINKS.map((l) => (
                                        <NavLink
                                            key={l.to}
                                            to={l.to}
                                            className="block px-4 py-2 text-xs tracking-widest uppercase text-silver-200 hover:bg-white/5 hover:text-white"
                                            data-testid={l.testid}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            {l.label}
                                        </NavLink>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </nav>

                <div className="flex items-center gap-2 md:gap-3">
                    {!user ? (
                        <>
                            <Link
                                to="/login"
                                className="hidden sm:inline-block text-xs tracking-widest uppercase text-silver-200 hover:text-white px-3 py-2"
                                data-testid="nav-login"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                className="text-xs md:text-sm bg-platinum hover:bg-platinum-200 text-ink-300 font-medium px-3 md:px-4 py-2 rounded-sm transition-colors whitespace-nowrap"
                                data-testid="nav-register"
                            >
                                Join
                            </Link>
                        </>
                    ) : (
                        <>
                            <div className="hidden xl:flex items-center gap-2 text-silver-300 text-sm" data-testid="nav-user-email">
                                {user.role === "admin" ? <ShieldCheck className="w-4 h-4 text-platinum" /> : user.role === "creator" ? <Film className="w-4 h-4 text-platinum" /> : <UserCircle2 className="w-4 h-4 text-platinum" />}
                                <span className="truncate max-w-[140px]">{user.email}</span>
                            </div>
                            <button
                                onClick={async () => {
                                    await logout();
                                    nav("/");
                                }}
                                className="hidden lg:flex items-center gap-2 text-silver-200 hover:text-white text-sm px-3 py-2 border border-white/10 rounded-sm"
                                data-testid="nav-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Logout
                            </button>
                        </>
                    )}

                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/10 rounded-sm text-silver-200"
                        aria-label="Toggle menu"
                        data-testid="nav-menu-toggle"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden overflow-hidden border-t border-white/5 bg-ink-200/95 backdrop-blur-xl"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col px-6 py-5 gap-3">
                            {[...primaryLinks, ...platformLinks, ...SECONDARY_LINKS].map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className={linkCls}
                                    data-testid={`m-${l.testid}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <div className="h-px bg-white/5 my-1" />
                            {!user ? (
                                <Link
                                    to="/login"
                                    onClick={() => setOpen(false)}
                                    className="text-sm text-silver-200 uppercase tracking-widest"
                                    data-testid="nav-mobile-login"
                                >
                                    Sign in
                                </Link>
                            ) : (
                                <>
                                    <div className="text-xs text-silver-500 uppercase tracking-widest">
                                        {user.email}
                                    </div>
                                    <button
                                        onClick={async () => {
                                            await logout();
                                            setOpen(false);
                                            nav("/");
                                        }}
                                        className="flex items-center gap-2 text-silver-200 hover:text-white text-sm"
                                        data-testid="nav-mobile-logout"
                                    >
                                        <LogOut className="w-4 h-4" /> Logout
                                    </button>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
